import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, BarChart2 } from "lucide-react";

import usePromoCodeService from "../../../api/payments/promoCodes/promoCode.service";
import useDiscountTierService from "../../../api/payments/discountTiers/discountTier.service";
import usePartnerService from "../../../api/payments/partners/partner.service";
import { PromoCode, PromoCodeStats } from "../../../api/payments/promoCodes/promoCode.responses";
import { DiscountTier } from "../../../api/payments/discountTiers/discountTier.responses";
import { Partner } from "../../../api/payments/partners/partner.responses";
import { Button } from "../../ui/Button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../ui/Dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/Table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/Select";
import { ActiveStatusBadge } from "../shared/ActiveStatusBadge";
import { DeactivateDialog } from "../shared/DeactivateDialog";
import { TabHeader } from "../shared/TabHeader";
import PromoForm from "./PromoForm";
import PromoCodeStatsDialog from "./PromoCodeStatsDialog";

const createSchema = z.object({
  code: z
    .string()
    .min(1, "Required")
    .regex(/^[A-Z0-9_-]+$/, "Use UPPERCASE letters, numbers, hyphens and underscores"),
  discountTierCode: z.string().min(1, "Required"),
  partnerId: z.string().optional(),
  maxRedemptions: z.coerce.number().int().positive().optional().or(z.literal("")),
  validFrom: z.string().optional().or(z.literal("")),
  expiresAt: z.string().optional().or(z.literal("")),
});

const editSchema = z.object({
  discountTierCode: z.string().min(1, "Required"),
  partnerId: z.string().optional(),
  maxRedemptions: z.coerce.number().int().positive().optional().or(z.literal("")),
  validFrom: z.string().optional().or(z.literal("")),
  expiresAt: z.string().optional().or(z.literal("")),
  isActive: z.boolean(),
});

type CreateFormValues = z.infer<typeof createSchema>;
type EditFormValues = z.infer<typeof editSchema>;

function PromoCodesTab() {
  const promoService = usePromoCodeService();
  const discountTierService = useDiscountTierService();
  const partnerService = usePartnerService();

  const [promos, setPromos] = useState<PromoCode[]>([]);
  const [tiers, setTiers] = useState<DiscountTier[]>([]);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editPromo, setEditPromo] = useState<PromoCode | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [stats, setStats] = useState<PromoCodeStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [filterActive, setFilterActive] = useState<string>("all");

  const createForm = useForm<CreateFormValues>({
    resolver: zodResolver(createSchema),
    defaultValues: {
      code: "",
      discountTierCode: "",
      partnerId: "",
      maxRedemptions: "",
      validFrom: "",
      expiresAt: "",
    },
  });

  const editForm = useForm<EditFormValues>({
    resolver: zodResolver(editSchema),
    defaultValues: {
      discountTierCode: "",
      partnerId: "",
      maxRedemptions: "",
      validFrom: "",
      expiresAt: "",
      isActive: true,
    },
  });

  const fetchPromos = () => {
    const isActiveParam =
      filterActive === "active"
        ? true
        : filterActive === "inactive"
        ? false
        : undefined;
    promoService
      .listPromoCodes({ isActive: isActiveParam })
      .then((res) => setPromos(res.data))
      .catch(() => toast.error("Failed to load promo codes"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    Promise.all([
      discountTierService.listDiscountTiers(),
      partnerService.listPartners(),
    ]).then(([tiersRes, partnersRes]) => {
      setTiers(tiersRes.data.filter((t) => t.isActive));
      setPartners(partnersRes.data.filter((p) => p.isActive));
    });
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchPromos();
  }, [filterActive]);

  const isoToInputDate = (iso: string | null) =>
    iso ? iso.slice(0, 16) : "";

  const handleCreate = async (values: CreateFormValues) => {
    setSubmitting(true);
    try {
      await promoService.createPromoCode({
        code: values.code.toUpperCase(),
        discountTierCode: values.discountTierCode,
        partnerId: values.partnerId || undefined,
        maxRedemptions: values.maxRedemptions
          ? Number(values.maxRedemptions)
          : undefined,
        validFrom: values.validFrom || undefined,
        expiresAt: values.expiresAt || undefined,
      });
      toast.success("Promo code created");
      setCreateOpen(false);
      createForm.reset();
      fetchPromos();
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ?? "Failed to create promo code"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditOpen = (promo: PromoCode) => {
    setEditPromo(promo);
    editForm.reset({
      discountTierCode: promo.discountTierCode,
      partnerId: promo.partnerId ?? "",
      maxRedemptions: promo.maxRedemptions ?? "",
      validFrom: isoToInputDate(promo.validFrom),
      expiresAt: isoToInputDate(promo.expiresAt),
      isActive: promo.isActive,
    });
  };

  const handleEdit = async (values: EditFormValues) => {
    if (!editPromo) return;
    setSubmitting(true);
    try {
      await promoService.updatePromoCode(editPromo.id, {
        discountTierCode: values.discountTierCode,
        partnerId: values.partnerId || null,
        maxRedemptions: values.maxRedemptions
          ? Number(values.maxRedemptions)
          : null,
        validFrom: values.validFrom || null,
        expiresAt: values.expiresAt || null,
        isActive: values.isActive,
      });
      toast.success("Promo code updated");
      setEditPromo(null);
      fetchPromos();
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ?? "Failed to update promo code"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeactivate = async () => {
    if (!deleteId) return;
    setSubmitting(true);
    try {
      await promoService.deactivatePromoCode(deleteId);
      toast.success("Promo code deactivated");
      setDeleteId(null);
      fetchPromos();
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ?? "Failed to deactivate promo code"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewStats = async (id: string) => {
    setStatsLoading(true);
    setStats(null);
    try {
      const res = await promoService.getPromoCodeStats(id);
      setStats(res.data);
    } catch {
      toast.error("Failed to load stats");
    } finally {
      setStatsLoading(false);
    }
  };

  const partnerName = (id: string | null) =>
    id ? (partners.find((p) => p.id === id)?.name ?? id) : "—";

  const tierName = (code: string) =>
    tiers.find((t) => t.code === code)?.name ?? code;

  return (
    <div className="space-y-4">
      <TabHeader title="Promo Codes">
        <Select value={filterActive} onValueChange={setFilterActive}>
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          New Code
        </Button>
      </TabHeader>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : promos.length === 0 ? (
        <p className="text-sm text-muted-foreground">No promo codes found.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Tier</TableHead>
              <TableHead>Partner</TableHead>
              <TableHead>Limit</TableHead>
              <TableHead>Expires</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {promos.map((promo) => (
              <TableRow key={promo.id}>
                <TableCell className="font-mono text-sm">{promo.code}</TableCell>
                <TableCell>{tierName(promo.discountTierCode)}</TableCell>
                <TableCell className="text-muted-foreground">
                  {partnerName(promo.partnerId)}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {promo.maxRedemptions ?? "∞"}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {promo.expiresAt
                    ? new Date(promo.expiresAt).toLocaleDateString()
                    : "Never"}
                </TableCell>
                <TableCell>
                  <ActiveStatusBadge isActive={promo.isActive} />
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      title="View stats"
                      onClick={() => handleViewStats(promo.id)}
                    >
                      <BarChart2 className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleEditOpen(promo)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    {promo.isActive && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-destructive hover:text-destructive"
                        onClick={() => setDeleteId(promo.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Promo Code</DialogTitle>
          </DialogHeader>
          <PromoForm
            form={createForm}
            onSubmit={handleCreate}
            onCancel={() => setCreateOpen(false)}
            tiers={tiers}
            partners={partners}
            submitting={submitting}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editPromo} onOpenChange={(o) => !o && setEditPromo(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              Edit Promo Code{" "}
              {editPromo && (
                <span className="font-mono text-base">{editPromo.code}</span>
              )}
            </DialogTitle>
          </DialogHeader>
          <PromoForm
            form={editForm}
            onSubmit={handleEdit}
            onCancel={() => setEditPromo(null)}
            isEdit
            tiers={tiers}
            partners={partners}
            submitting={submitting}
          />
        </DialogContent>
      </Dialog>

      <PromoCodeStatsDialog
        open={stats !== null || statsLoading}
        onClose={() => setStats(null)}
        stats={stats}
        statsLoading={statsLoading}
      />

      <DeactivateDialog
        open={!!deleteId}
        onOpenChange={(o) => !o && setDeleteId(null)}
        title="Deactivate Promo Code"
        description="The code will no longer be usable by new customers. Existing applications won't be affected."
        onConfirm={handleDeactivate}
        submitting={submitting}
      />
    </div>
  );
}

export default PromoCodesTab;
